import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { pitchesApi } from "../../repositories/pitches";
import { useAppSelector } from "../../hooks/redux";
import { formatDisplayDate } from "../../DashboardPage/utils/dates";
import { ChevronLeft, ChevronRight, Copy, Check, Search, X } from "lucide-react";
import  {TrashIcon} from "@heroicons/react/24/outline"
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal"
import Swal from "sweetalert2";

interface Pitch {
  id: number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  userId: number;
  trackISRC: string;
  mainGenre?: string;
  configuration?: string;
  companyName?: string;

}

const PastSubmissions: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"artist" | "release" | "company">("artist");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPitchIds, setSelectedPitchIds] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const pageSize = 10;
  const isAdmin=user?.role === "admin";
  const fetchPastPitches = async () => {
      try {
        const response = await pitchesApi.getAll({
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          searchType: searchType,
          past: true,
        });

        const fetchedPitches: Pitch[] = response.data.data || [];
        const sorted = fetchedPitches.sort(
          (a, b) =>
            new Date(b.releaseDate).getTime() -
            new Date(a.releaseDate).getTime()
        );

        setPitches(sorted);
        setTotalPages(
          Math.ceil(response.data.pagination.totalItems / pageSize) || 1
        );
      } catch (error) {
        console.error("Failed to fetch pitches:", error);
        setPitches([]);
      }
    };
  useEffect(() => {
    fetchPastPitches();
  }, [currentPage, searchTerm, searchType, user]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };
  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value as "artist" | "release" | "company");
    setCurrentPage(1); // Reset to first page on new search type
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };;

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const copyToClipboard = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields((prev) => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => {
        setCopiedFields((prev) => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDeleteConfirmed = async () => {
    setModalLoading(true);
    try {
      const response = await pitchesApi.deletePitch(selectedPitchIds);
      setSelectedPitchIds([]);
      setShowDeleteModal(false);
      setDeleteInput("");
      setModalLoading(false);
      fetchPastPitches();
      Swal.fire({
        title: response?.data?.success ? "Deleted!" : "Error!",
        text: response?.data?.message || "Pitch deleted successfully!",
        icon: response?.data?.success ? "success" : "error",
        confirmButtonText: "OK",
      });
    } catch (error: any) {
      setShowDeleteModal(false);
      setDeleteInput("");
      setModalLoading(false);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="p-6 mx-auto max-w-7xl">
      <div className="flex flex-col mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium text-gray-800">Past Submissions</h1>
          <p className="mt-2 text-gray-600">
            View and manage your past music submissions
          </p>
        </div>
        

        {/* Search Section */}
        <div className="flex flex-col gap-3 mt-4 sm:mt-0 sm:flex-row">
          {selectedPitchIds.length > 0 && (
  <div className="flex items-center space-x-4  pl-4">
              <span className="text-sm text-gray-700">
                {selectedPitchIds.length} selected
              </span>
              <button
                onClick={() =>setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 rounded-md  hover:bg-red-50 focus:ring-2 focus:ring-red-200 transition"
                aria-label="Delete selected pitches"
              >
                <TrashIcon className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-600 font-semibold">Delete Selected Pitches</span>
              </button>
            </div>
)}
          {/* Search Type Selector */}
          <div className="relative">
            <select
              value={searchType}
              onChange={handleSearchTypeChange}
              className="px-3 py-2 pr-8 text-sm bg-white border border-gray-300 appearance-none rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="artist">Artist Name</option>
              <option value="release">Release Name</option>
              {
                user?.role === "admin" && <option value="company">Company Name</option>
              }
             
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative flex-1 sm:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={`Search by ${searchType === "artist" ? "artist name" : "release name"}...`}
              className="block w-full py-2 pl-10 pr-10 text-sm placeholder-gray-500 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div className="p-3 mb-4 border border-blue-200 rounded-md bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Search className="w-4 h-4 mr-2 text-blue-600" />
              <span className="text-sm text-blue-800">
                {isSearching ? "Searching..." : `Found ${pitches.length} result${pitches.length !== 1 ? 's' : ''} for "${searchTerm}" in ${searchType === "artist"
                    ? "artist names"
                    : searchType === "release"
                      ? "release names"
                      : "company names"
                  }`}
              </span>
            </div>
            {pitches.length > 0 && (
              <button
                onClick={clearSearch}
                className="text-sm text-blue-600 underline hover:text-blue-800"
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      )}

      {pitches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-lg shadow">
          <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            {searchTerm ? "No search results found" : "No past submissions yet"}
          </h3>
          <p className="max-w-sm text-gray-500">
            {searchTerm
              ? `No submissions found matching "${searchTerm}" in ${searchType === "artist" ? "artist names" : "release names"}. Try adjusting your search terms.`
              : "Once your first release date passes, you'll see it here along with analytics and review options."
            }
          </p>
        </div>
      ) : (
        <div className="overflow-hidden bg-white rounded-lg shadow">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {
                    isAdmin && <th className=" px-6 py-4 text-start">
                    <input
                      type="checkbox"
                      checked={pitches.length > 0 && pitches.every(p => selectedPitchIds.includes(p.id))}
                      onChange={e => {
                        if (e.target.checked) {
                          // Add current page IDs to selectedPitchIds (avoid duplicates)
                          setSelectedPitchIds(prev =>
                            Array.from(new Set([...prev, ...pitches.map(p => p.id)]))
                          );
                        } else {
                          // Remove only current page IDs from selectedPitchIds
                          setSelectedPitchIds(prev =>
                            prev.filter(id => !pitches.map(p => p.id).includes(id))
                          );
                        }
                      }}
                      aria-label="Select all"
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                  </th>
                  }
                   {isAdmin &&<th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Company Name
                  </th>}
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Artist
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Release
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap"
                  >
                    Release Date
                  </th>
                 
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pitches.map((pitch, index) => (
                  <tr
                    key={pitch.id}
                    className={`transition-colors hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-25"
                      }`}
                  >
                    {
                      isAdmin && <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPitchIds.includes(pitch.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedPitchIds(prev => [...prev, pitch.id]);
                          } else {
                            setSelectedPitchIds(prev => prev.filter(id => id !== pitch.id));
                          }
                        }}
                        aria-label={`Select ${pitch.artistName} - ${pitch.releaseTitle}`}
                        className="form-checkbox h-5 w-5 text-blue-600"
                      />
                    </td>
                    }
                    { user?.role==="admin" && (
                     <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pitch.companyName}
                      </div>
                    </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pitch.artistName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs text-sm text-gray-900">
                        <div
                          className="font-medium truncate"
                          title={pitch.releaseTitle}
                        >
                          {pitch.releaseTitle}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDisplayDate(pitch.releaseDate)}
                      </div>
                    </td>
                   
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-2">
                        <Link to={`/pitch/submission/${pitch.id}`}>
                          <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-md hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                            Review Pitch
                          </button>
                        </Link>
                        {pitch.trackISRC && (
                          <Link to={`/analytics/${pitch.id}`}>
                            <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-md hover:bg-teal-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                              Analytics
                            </button>
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Fill empty rows to maintain consistent height */}
                {pitches.length < pageSize &&
                  [...Array(pageSize - pitches.length)].map((_, index) => (
                    <tr key={`empty-${index}`} className="h-16">
                      <td colSpan={7} className="px-6 py-4"></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center text-sm text-gray-700">
              <p className="hidden sm:block">
                Showing{" "}
                <span className="font-medium">
                  {pitches.length > 0 ? 1 : 0}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {pitches.length}
                </span>{" "}
                of <span className="font-medium">{pitches.length}</span> results
                {searchTerm && (
                  <span className="ml-2 text-gray-500">
                    (filtered from {pitches.length} total)
                  </span>
                )}
              </p>
              <p className="sm:hidden">
                {pitches.length} result{pitches.length !== 1 ? 's' : ''}
                {searchTerm && ` (filtered)`}
              </p>
            </div>

            {/* Pagination controls - only show when not searching */}
            {!searchTerm && (
              <div className="flex items-center space-x-1">
                {/* First Page */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="First page"
                >
                  <ChevronLeft size={16} className="mr-0.5" />
                  <ChevronLeft size={16} />
                </button>

                {/* Previous Page */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Page Numbers */}
                <div className="items-center hidden md:flex">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    const isCurrentPage = pageNum === currentPage;

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium transition-colors focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isCurrentPage
                          ? "z-10 bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {totalPages > 5 && (
                    <>
                      <span className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300">
                        ...
                      </span>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Page */}
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      prev < totalPages ? prev + 1 : prev
                    )
                  }
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  <ChevronRight size={16} />
                </button>

                {/* Last Page */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Last page"
                >
                  <ChevronRight size={16} />
                  <ChevronRight size={16} className="ml-0.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirmed}
          inputValue={deleteInput}
          setInputValue={setDeleteInput}
          loading={modalLoading}
        />
      )}
    </div>
  );
};

export default PastSubmissions;
